import { useMemo } from 'react';

import type { ServiceCategoryRow } from '@/api/booking/getServiceCategoriesQuery';
import type { Service } from '@/types/booking';
import { getServiceGroups } from '@/utils/booking/getServiceGroups';
import { getServiceListMode } from '@/utils/booking/getServiceListMode';

interface UseFlatServiceGroupsParams {
  /** Category-first is on and no category is selected. */
  isUnfiltered: boolean;
  /** The route's own category is selected, which hides the select too. */
  isFiltered: boolean;
  /** True for "any professional", as the L1 category list counts it. */
  requireBarberAssignment: boolean;
  /**
   * Every service of the catalog, before the cart narrows it, with
   * uncategorized services assigned to `Other`.
   */
  allServices?: Service[];
  /** The services the list shows, after the cart narrowing. */
  services: Service[];
  rows?: ServiceCategoryRow[];
}

/**
 * The grouped flat layout applies on the unfiltered category-first screen
 * for a small catalog. The size is judged on `allServices` so a cart that
 * narrows the list never flips the layout. `groups` is undefined otherwise.
 * `rows` is undefined until the categories load, which is never flat.
 */
export function useFlatServiceGroups({
  isUnfiltered,
  isFiltered,
  requireBarberAssignment,
  allServices,
  services,
  rows,
}: UseFlatServiceGroupsParams) {
  const isFlat =
    isUnfiltered &&
    !!allServices &&
    !!rows &&
    getServiceListMode({
      services: allServices,
      rows,
      requireBarberAssignment,
    }) === 'flat';

  const groups = useMemo(
    () => (isFlat && rows ? getServiceGroups({ services, rows }) : undefined),
    [isFlat, services, rows],
  );

  return { hideCategorySelect: isFiltered || isFlat, groups, isFlat };
}
